import {
  IPublicClientApplication,
  InteractionRequiredAuthError,
  AccountInfo,
} from '@azure/msal-browser';

interface TokenRequest {
  scopes: string[];
  account: AccountInfo;
}

/**
 * TokenManager singleton to prevent MSAL race conditions.
 * Ensures only one token acquisition happens at a time.
 * Now includes proactive token refresh to prevent mid-session expirations.
 */
class TokenManager {
  private static instance: TokenManager;
  private tokenPromise: Promise<string> | null = null;
  private msalInstance: IPublicClientApplication | null = null;
  private lastToken: string | null = null;
  private lastTokenExpiry: number = 0;
  private refreshTimer: NodeJS.Timeout | null = null;
  private lastRequest: TokenRequest | null = null;

  private constructor() {}

  static getInstance(instance: IPublicClientApplication): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    if (instance) {
      TokenManager.instance.msalInstance = instance;
    }
    return TokenManager.instance;
  }

  /**
   * Global lock for blocking token acquisition during sensitive operations (e.g., redirect handling)
   */
  private static globalLock: Promise<void> | null = null;

  static setGlobalLock(promise: Promise<void>): void {
    TokenManager.globalLock = promise;
  }

  async getToken(request: TokenRequest): Promise<string> {
    // Store the last request for proactive refresh
    this.lastRequest = request;

    // Wait for global lock (e.g., redirect handling) to complete
    if (TokenManager.globalLock) {
      console.log('[TokenManager] Waiting for global lock...');
      await TokenManager.globalLock;
    }

    // Return cached token if still valid (with 1-minute buffer)
    if (this.lastToken && Date.now() < this.lastTokenExpiry - 60000) {
      console.log('[TokenManager] Using cached token');
      return this.lastToken;
    }

    // If token acquisition already in progress, wait for it
    if (this.tokenPromise) {
      console.log('[TokenManager] Token acquisition in progress, waiting...');
      return this.tokenPromise;
    }

    // Start new token acquisition
    this.tokenPromise = this.acquireToken(request);

    try {
      const token = await this.tokenPromise;
      return token;
    } finally {
      // Clear the promise after completion (success or failure)
      this.tokenPromise = null;
    }
  }

  private async acquireToken(request: TokenRequest): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('[TokenManager] MSAL instance not initialized');
    }

    try {
      console.log('[TokenManager] Acquiring token silently...');
      const response = await this.msalInstance.acquireTokenSilent(request);

      // Cache the token
      this.lastToken = response.accessToken;
      this.lastTokenExpiry = response.expiresOn?.getTime() || 0;

      // Schedule proactive refresh 5 minutes before expiry
      this.scheduleTokenRefresh();

      console.log('[TokenManager] Token acquired successfully');
      return response.accessToken;
    } catch (error) {
      // Check if this is an interaction_in_progress error and retry once
      const errorCode =
        (error as { errorCode?: string; errorMessage?: string })?.errorCode ||
        (error as { errorCode?: string; errorMessage?: string })?.errorMessage;
      if (
        errorCode === 'interaction_in_progress' ||
        (typeof errorCode === 'string' &&
          errorCode.includes('interaction_in_progress'))
      ) {
        console.warn(
          '[TokenManager] Interaction already in progress, retrying after delay...'
        );
        // Wait 1 second and retry once
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.acquireToken(request);
      }

      // Handle interaction required errors
      if (error instanceof InteractionRequiredAuthError) {
        console.log(
          '[TokenManager] Interactive login required, triggering redirect...'
        );
        this.msalInstance.loginRedirect(request);
        throw new Error('Redirecting to login...');
      }

      throw error;
    }
  }

  clearCache(): void {
    console.log('[TokenManager] Clearing token cache');
    this.lastToken = null;
    this.lastTokenExpiry = 0;
    this.tokenPromise = null;
    this.lastRequest = null;
    this.cancelRefreshTimer();
  }

  /**
   * Schedule proactive token refresh 5 minutes before expiration.
   * This prevents mid-session authentication prompts and improves UX.
   */
  private scheduleTokenRefresh(): void {
    // Cancel any existing timer
    this.cancelRefreshTimer();

    // Don't schedule if no token or expiry
    if (!this.lastToken || !this.lastTokenExpiry) {
      return;
    }

    const now = Date.now();
    const expiresIn = this.lastTokenExpiry - now;

    // Only schedule if token expires more than 5 minutes from now
    if (expiresIn <= 300000) {
      // 5 minutes in ms
      console.log(
        '[TokenManager] Token expires in less than 5 minutes, not scheduling refresh'
      );
      return;
    }

    // Schedule refresh 5 minutes before expiry
    const refreshIn = expiresIn - 300000; // Refresh 5 min before expiry
    const refreshTime = new Date(now + refreshIn);

    console.log(
      `[TokenManager] Scheduling proactive token refresh in ${Math.round(refreshIn / 60000)} minutes (at ${refreshTime.toLocaleTimeString()})`
    );

    this.refreshTimer = setTimeout(() => {
      this.proactiveRefresh();
    }, refreshIn);
  }

  /**
   * Proactively refresh the token in the background.
   * This happens automatically 5 minutes before expiration.
   */
  private async proactiveRefresh(): Promise<void> {
    if (!this.lastRequest) {
      console.warn('[TokenManager] No previous request saved for refresh');
      return;
    }

    try {
      console.log('[TokenManager] Starting proactive token refresh...');
      await this.getToken(this.lastRequest);
      console.log('[TokenManager] Proactive token refresh successful');
    } catch (error) {
      console.error('[TokenManager] Proactive refresh failed:', error);
      // Don't throw - this is background refresh, errors will surface on next API call
    }
  }

  /**
   * Cancel the scheduled refresh timer.
   */
  private cancelRefreshTimer(): void {
    if (this.refreshTimer) {
      console.log('[TokenManager] Canceling scheduled token refresh');
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

export { TokenManager };
